import React, { useState } from 'react';
import { t } from '../../i18n';
import Modal from '../ui/Modal';
import { TableScreenSheet } from './TableScreenSheet';
import HostToolsSheet from './HostToolsSheet';
import { PlayerControls, HostControls, DealWaitingAwayNote } from './GameParts';
import { RebuyAskForm, RebuyWaitingCard, SpectatingPill, PendingRebuyRows } from './RebuyAskCard';
import { isChipless, isViewerHost, seatedPlayers, seatPresence } from '../../utils/playerLedger';
import { SIT_OUT_COPY } from '../../utils/sitOutCopy';
import { useFormatCurrency } from '../../utils/currencyContext';

// The action band, shared by both game-screen layouts.
//
// This block is deliberately ONE component rather than two copies: it decides
// mutually exclusively between auto-showdown notice / chipless notice / player
// controls / host controls / a quiet waiting state. Duplicating that decision
// per layout is exactly how the two views would drift apart.

function ActionArea({
  gameState, game, currentStage, currentPlayer, currentPlayerId, hostId, playersArray,
  totalPot, toCall, minRaise, potRaise, allInAmount,
  raiseAmount, setRaiseAmount, isPerformingAction,
  onFold, onCall, onRaise, onAllIn, onShowRaisePanel,
  onDeclareWinner, canDeclareWinner, isDeclaringWinner,
  tapWinnerMode, awardLabel, awardEnabled, onConfirmWinner,
  onAdvanceStage, checkFoldArmed, onToggleCheckFold,
  hostDealFallback, canDealNextHand = true, onDealNextHand, dealBlockedByAway = false,
  onImBack,
  pendingRebuys = [], defaultBuyInLabel = '',
  onRequestRebuy, onCancelRebuy, onAddRebuy, onSpectate, onReopenRebuy,
  rebuyAskOpen = true, requestingRebuy = false, cancellingRebuy = false, addingRebuyId = null,
}) {
  const viewerIsHost = isViewerHost(currentPlayerId, hostId);
  const formatCurrency = useFormatCurrency();
  const myRequest = pendingRebuys.find((r) => r.playerId === currentPlayerId);
  const handOver = Array.isArray(game?.winnerInfo?.winnerIds) && game.winnerInfo.winnerIds.length > 0;
  const allInThisHand = !!(currentPlayer?.isAllIn && currentPlayer?.isActive !== false && !handOver);
  const showChiplessUi = isChipless(currentPlayer) && !allInThisHand && (
    handOver || currentPlayer?.isActive === false || (currentStage === 'SHOWDOWN' && totalPot === 0)
  );

  const chiplessCard = showChiplessUi ? (
    myRequest ? (
      <RebuyWaitingCard
        amountLabel={formatCurrency(myRequest.amount)}
        onCancel={onCancelRebuy}
        cancelling={cancellingRebuy}
      />
    ) : rebuyAskOpen ? (
      <RebuyAskForm
        defaultAmount={defaultBuyInLabel}
        onRequest={onRequestRebuy}
        onSpectate={onSpectate}
        submitting={requestingRebuy}
      />
    ) : (
      <SpectatingPill onRebuy={onReopenRebuy} />
    )
  ) : null;
  // HostHandResult already mounts the amount form. A second copy in this
  // slot sits under the z-60 overlay and intercepts e2e clicks after Got it.
  // Ternary (not &&) so we still return the element, not boolean `true`.
  const slotChipless = viewerIsHost && handOver ? null : chiplessCard;

  return (
    <>
      {(() => {
        // Hand is over but the hand-result overlay isn't up on this device:
        // Deal next hand lives in the action slot instead, so the host can
        // never be left with "Deal the next hand when ready" and no button.
        if (viewerIsHost && hostDealFallback && onDealNextHand) {
          return (
            <div className="space-y-2">
              {pendingRebuys.length > 0 && (
                <PendingRebuyRows requests={pendingRebuys} onAdd={onAddRebuy} addingId={addingRebuyId} />
              )}
              {chiplessCard}
              <button
                onClick={onDealNextHand}
                disabled={!canDealNextHand}
                style={{ WebkitTapHighlightColor: 'transparent' }}
                className="w-full h-14 rounded-[14px] bg-ink text-cream text-[15px] font-semibold tracking-[-0.005em] flex items-center justify-center disabled:bg-line disabled:text-mute"
                data-testid="deal-next-hand-slot"
              >
                {t('game.dealNextHand')}
              </button>
              {!canDealNextHand && dealBlockedByAway && <DealWaitingAwayNote />}
            </div>
          );
        }

        // Busted player: rebuy / spectate in the action slot (not during an
        // all-in that's still being awarded). Must beat the auto-showdown
        // "All players all-in" line — that reason stays on gameState after
        // the pot is awarded, and would hide the form.
        if (slotChipless && !viewerIsHost) {
          return slotChipless;
        }

        // Check for auto-showdown scenarios. Skip once the hand is over —
        // winnerInfo is the post-hand state; this copy is for the runout.
        const showAutoShowdownMessage = !handOver && currentStage === 'SHOWDOWN' &&
          game?.autoAdvancedReason &&
          ['BETTING_CAPPED_BY_ALL_IN', 'ALL_REMAINING_ALL_IN', 'LAST_PLAYER_RESPONDED'].includes(game.autoAdvancedReason);

        // Auto-runout copy must not swallow Award / Declare Winner. When the
        // host can still pick a winner, fall through to HostControls so a
        // remount at showdown (refresh) with autoAdvancedReason still set
        // gets tap-the-winner or the side-pot pop-up immediately.
        if (showAutoShowdownMessage && !canDeclareWinner) {
          const getAutoShowdownTitle = (reason) => {
            if (reason === 'ALL_REMAINING_ALL_IN') return t('game.allPlayersAllIn');
            return t('game.bettingComplete');
          };
          return (
            <div className="space-y-2">
              <p className="text-[13.5px] text-mute text-center">{getAutoShowdownTitle(game.autoAdvancedReason)}</p>
            </div>
          );
        }

        const presence = seatPresence(currentPlayer);
        const away = presence === 'away';
        const pending = presence === 'pending';
        const waitingNextHand = presence === 'next';

        // Show player controls if it's this player's turn (including host)
        const isMyTurn = presence === 'live' && game?.currentTurnPlayerId === currentPlayerId;

        // Show host controls only when game management is needed
        const needsHostControls = viewerIsHost && (
          !game?.currentTurnPlayerId || // No active turn (betting round complete)
          game?.canAdvanceStage || // Betting round complete, can advance stage
          canDeclareWinner || // Can declare winner
          currentStage === 'SHOWDOWN' // At showdown
        );

        if (isMyTurn) {
          // Player's turn - show action buttons (even for host)
          if (slotChipless) return slotChipless;

          return (
            <PlayerControls
              gameState={gameState}
              currentPlayerId={currentPlayerId}
              toCall={toCall}
              potAmount={totalPot}
              minRaise={minRaise}
              potRaise={potRaise}
              allInAmount={allInAmount}
              raiseAmount={raiseAmount}
              setRaiseAmount={setRaiseAmount}
              onFold={onFold}
              onCall={onCall}
              onRaise={onRaise}
              onAllIn={onAllIn}
              onShowRaisePanel={onShowRaisePanel}
              isPerformingAction={isPerformingAction}
            />
          );
        } else if (
          // Not your turn but still in the hand: keep the buttons on screen,
          // Fold/Raise dimmed, middle one is the Check/Fold pre-action.
          presence === 'live' && currentPlayer && game?.currentTurnPlayerId &&
          currentStage !== 'SHOWDOWN' && !currentPlayer.hasFolded && !currentPlayer.isAllIn &&
          currentPlayer.isActive !== false && (currentPlayer.stack || 0) > 0 &&
          !needsHostControls && onToggleCheckFold
        ) {
          return (
            <PlayerControls
              preAction
              checkFoldArmed={!!checkFoldArmed}
              onToggleCheckFold={onToggleCheckFold}
              toCall={toCall}
            />
          );
        } else if (away || pending) {
          const imBack = onImBack ? (
            <div className="space-y-1.5" data-testid="im-back-slot">
              <button
                type="button"
                onClick={onImBack}
                style={{ WebkitTapHighlightColor: 'transparent' }}
                className="w-full h-14 rounded-[14px] bg-ink text-cream text-[15px] font-semibold tracking-[-0.005em] flex items-center justify-center"
                data-testid="im-back-btn"
              >
                {SIT_OUT_COPY.returnButton}
              </button>
              <p className="text-[12.5px] text-mute text-center m-0" data-testid="im-back-hint">
                {pending ? SIT_OUT_COPY.returnHintPending : SIT_OUT_COPY.returnHint}
              </p>
            </div>
          ) : null;
          if (needsHostControls) {
            return (
              <div className="space-y-2">
                {imBack}
                {slotChipless}
                {viewerIsHost && !handOver && pendingRebuys.length > 0 && (
                  <PendingRebuyRows requests={pendingRebuys} onAdd={onAddRebuy} addingId={addingRebuyId} />
                )}
                <HostControls
                  gameState={gameState}
                  currentPlayerId={currentPlayerId}
                  onAdvanceStage={onAdvanceStage}
                  onDeclareWinner={onDeclareWinner}
                  canDeclareWinner={canDeclareWinner}
                  isDeclaringWinner={isDeclaringWinner}
                  tapWinnerMode={tapWinnerMode}
                  awardLabel={awardLabel}
                  awardEnabled={awardEnabled}
                  onConfirmWinner={onConfirmWinner}
                />
              </div>
            );
          }
          return imBack;
        } else if (slotChipless) {
          return slotChipless;
        } else if (waitingNextHand) {
          return (
            <div className="py-3 text-center" data-testid="next-hand-note">
              <p className="text-[13px] text-mute m-0">{t('game.youreInNext')}</p>
            </div>
          );
        } else if (needsHostControls) {
          return (
            <div className="space-y-2">
              {slotChipless}
              {viewerIsHost && !handOver && pendingRebuys.length > 0 && (
                <PendingRebuyRows requests={pendingRebuys} onAdd={onAddRebuy} addingId={addingRebuyId} />
              )}
              <HostControls
                gameState={gameState}
                currentPlayerId={currentPlayerId}
                onAdvanceStage={onAdvanceStage}
                onDeclareWinner={onDeclareWinner}
                canDeclareWinner={canDeclareWinner}
                isDeclaringWinner={isDeclaringWinner}
                tapWinnerMode={tapWinnerMode}
                awardLabel={awardLabel}
                awardEnabled={awardEnabled}
                onConfirmWinner={onConfirmWinner}
              />
            </div>
          );
        } else {
          const currentPlayerTurn = playersArray.find(p => p.id === game?.currentTurnPlayerId);

          if (currentPlayerTurn) {
            return null;
          } else {
            return null;
          }
        }
      })()}
    </>
  );
}

const HOST_TOOLS_PILL =
  'bg-transparent text-[12px] font-medium px-3 py-1 rounded-full border border-line hover:text-ink hover:border-ink transition-colors';

// Host-only utility row: a single Host tools pill. Feedback / Hands / History /
// Leave / Balances live in the header Menu. Non-hosts see nothing here.
//
// Rebuys / Change blinds / Settle Up / Make host live in this same sheet —
// GameBoard owns `toolsOpen` so the post-hand Host tools pill opens it too.
// z-[80] sits above the winner overlay (z-60). Add or rejoin is z-[90] on top
// so Back returns here; X on that sheet closes both.
function UtilityRow({
  currentPlayerId, hostId, canUndo, undoSnapshot, onOpenUndo, onOpenMoveChips,
  onOpenRemovePlayer, onOpenAddOrRejoin, onOpenInvite, onOpenRejoinPlayer, onOpenRebuy, onOpenChangeBlinds,
  onEndGame, onOpenMakeHost, toolsOpen: toolsOpenProp, onToolsOpenChange, tight = false,
  gameState,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [tableScreenOpen, setTableScreenOpen] = useState(false);
  const formatCurrency = useFormatCurrency();
  const controlled = toolsOpenProp !== undefined;
  const toolsOpen = controlled ? toolsOpenProp : internalOpen;
  const setToolsOpen = (next) => {
    if (!controlled) setInternalOpen(next);
    onToolsOpenChange?.(next);
  };
  const viewerIsHost = isViewerHost(currentPlayerId, hostId);
  const roomCode = gameState?.roomCode;
  const closeAnd = (fn) => () => {
    setToolsOpen(false);
    fn?.();
  };
  const openAddOrRejoin = onOpenAddOrRejoin || onOpenInvite || onOpenRejoinPlayer;
  const seated = seatedPlayers(gameState?.players);
  const seatedCount = seated.length;
  const bustCount = seated.filter((p) => (p.stack || 0) <= 0).length;
  const settings = gameState?.settings;
  const blindsLabel = `${formatCurrency(settings?.smallBlind || 0)} / ${formatCurrency(settings?.bigBlind || 0)}`;

  if (!viewerIsHost) return null;

  return (
    <>
      <div
        className={`${tight ? 'mt-2' : 'mt-4'} flex items-center justify-center gap-2 flex-nowrap`}
        data-testid="utility-row"
      >
        <button
          type="button"
          onClick={() => setToolsOpen(true)}
          style={{ WebkitTapHighlightColor: 'transparent' }}
          className={`${HOST_TOOLS_PILL} text-mute flex items-center gap-1.5`}
          data-testid="host-tools-pill"
        >
          {t('game.hostTools')}
          {/* Dot space is always reserved so the row doesn't shift sideways when Undo becomes available. */}
          <span className="w-1.5 h-1.5 rounded-full bg-ink" style={{ visibility: canUndo ? 'visible' : 'hidden' }} aria-hidden="true" />
        </button>
      </div>

      <TableScreenSheet
        open={tableScreenOpen}
        roomCode={roomCode}
        onClose={() => setTableScreenOpen(false)}
      />

      <Modal
        open={!!toolsOpen}
        onClose={() => setToolsOpen(false)}
        title={t('game.hostTools')}
        compact
        zClass="z-[80]"
        testId="host-tools"
      >
        <HostToolsSheet
          canUndo={canUndo}
          undoSnapshot={undoSnapshot}
          blindsLabel={blindsLabel}
          seatedCount={seatedCount}
          bustCount={bustCount}
          onOpenUndo={closeAnd(onOpenUndo)}
          onOpenChangeBlinds={onOpenChangeBlinds ? closeAnd(onOpenChangeBlinds) : undefined}
          onOpenTableScreen={roomCode ? () => setTableScreenOpen(true) : undefined}
          onOpenAddOrRejoin={openAddOrRejoin || undefined}
          onOpenRebuy={onOpenRebuy ? closeAnd(onOpenRebuy) : undefined}
          onOpenMoveChips={closeAnd(onOpenMoveChips)}
          onOpenMakeHost={onOpenMakeHost ? closeAnd(onOpenMakeHost) : undefined}
          onOpenRemovePlayer={onOpenRemovePlayer ? closeAnd(onOpenRemovePlayer) : undefined}
          onEndGame={onEndGame ? closeAnd(onEndGame) : undefined}
        />
      </Modal>
    </>
  );
}

export { ActionArea, UtilityRow };
